import React, { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react'

import { Layout } from 'components/Layout'
import { Text } from 'components/Text'
import { TextField } from 'components/TextField'
import type { TablePaginationActionsProps } from 'components/TablePagination/TablePagination.types'
import styled from 'styled-components'
import { Icon, IconProps } from 'components/Icon'
import Keyboard from 'utils/toolset/keyboard'
import { NoPaddingButton } from './TablePagination.styles'
import { prop } from 'tools/index'

export const ActionIcon = styled(Icon).attrs({
  color: 'neutral-darker',
  size: '16',
})<IconProps & { rotate?: number }>`
  transform: rotate(${prop('rotate', 0)}deg);
`

function TablePaginationActions({
  variant = 'default',
  disabled = false,
  onPageChange,
  page,
  count,
  rowsPerPage,
}: TablePaginationActionsProps): JSX.Element {
  const totalPages = Math.ceil(count / rowsPerPage)
  const [pageValue, setPageValue] = useState<number | ''>(page + 1)
  const isCompact = variant === 'compact'

  useEffect(() => {
    setPageValue(page + 1)
  }, [page])

  const handleFirstPageClick = () => {
    onPageChange(0)
  }

  const handlePreviousPageClick = () => {
    onPageChange(page - 1)
  }

  const handleNextPageClick = () => {
    onPageChange(page + 1)
  }

  const handleLastPageClick = () => {
    onPageChange(totalPages - 1)
  }

  const publishPageChange = () => {
    if (pageValue && pageValue - 1 !== page) {
      onPageChange(pageValue - 1)
    }
  }

  const handleKeyUp = (e: KeyboardEvent) => {
    if (Keyboard(e).is('ENTER')) {
      publishPageChange()
    }
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (Keyboard(e).is(['E_LOWERCASE', 'DOT', 'MINUS', 'PLUS'])) {
      e.preventDefault()
    }
  }

  const handlePageChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === '') {
      setPageValue('')
      return
    }

    const numberValue = Number(e.target.value)

    if (!numberValue || numberValue < 1 || numberValue > totalPages) return

    setPageValue(numberValue)
  }

  return (
    <Layout.Group space="s" align="center">
      {!isCompact && (
        <NoPaddingButton
          onClick={handleFirstPageClick}
          disabled={page === 0 || disabled}
          title="First page"
        >
          <ActionIcon name="caret-right-last" rotate={180} />
        </NoPaddingButton>
      )}
      <NoPaddingButton
        onClick={handlePreviousPageClick}
        disabled={page === 0 || disabled}
        title="Previous page"
      >
        <ActionIcon name="caret-left" />
      </NoPaddingButton>
      {!isCompact && (
        <>
          <TextField
            type="number"
            min={1}
            max={totalPages}
            disabled={disabled || totalPages === 1}
            onChange={handlePageChange}
            onBlur={publishPageChange}
            onKeyUp={handleKeyUp}
            onKeyDown={handleKeyDown}
            scale="small"
            value={count ? pageValue : 0}
            title="Page"
          />
          <Text variant="body" color={disabled ? 'color-neutral' : 'color-neutral-dark'}>
            of{' '}
            <Text variant="body-bold" color={disabled ? 'color-neutral' : 'color-neutral-dark'}>
              {totalPages}
            </Text>
          </Text>
        </>
      )}
      <NoPaddingButton
        onClick={handleNextPageClick}
        disabled={page >= totalPages - 1 || disabled}
        title="Next page"
      >
        <ActionIcon name="caret-right" />
      </NoPaddingButton>
      {!isCompact && (
        <NoPaddingButton
          onClick={handleLastPageClick}
          disabled={page >= totalPages - 1 || disabled}
          title="Last page"
        >
          <ActionIcon name="caret-right-last" />
        </NoPaddingButton>
      )}
    </Layout.Group>
  )
}

export default TablePaginationActions
